import { 
  FormControl, 
  FormLabel, 
  Grid, 
  Button, 
  TextField, 
  MenuItem,
  Alert
} from '@mui/material';
import { ProgramOffice, ProgramOfficeStatus } from '../../services/Types';
import { useAllUsers } from '../../hooks/useAllUsers';
import { useEffect, useState } from 'react';
import { useProgramOfficeData } from '../../hooks/useProgramOfficeData';
import { ApproverCheckboxes } from '../Checkboxes/Approver';
import { useToast } from '../../hooks/useToast';

interface ProgramOfficeEditFormProps {
  accountSelected: ProgramOffice;
  onSave?: () => void;
  onDelete?: () => void;
}

export const ProgramOfficeEditForm = ({ accountSelected, onSave, onDelete }: ProgramOfficeEditFormProps) => {
  const { allApproverUsers, errorUseAllUsers } = useAllUsers();
  const [editedAccount, setEditedAccount] = useState<ProgramOffice>(accountSelected);
  const { updateProgramOffice, resetProgramOfficeCache } = useProgramOfficeData();
  const [localError, setLocalError] = useState<string | null>(null);
  const { showToast, renderToast } = useToast();

  useEffect(() => {
    setEditedAccount(accountSelected);
    setLocalError(null);
  }, [accountSelected]);

  const handleSave = async () => {
    if (editedAccount.id) {
      const success = await updateProgramOffice(
        editedAccount.id,
        editedAccount.title,
        editedAccount.acronym,
        'ACTIVE',
        editedAccount.levelOneApprover
      );
      if (success) {
        setLocalError(null);
        showToast(true, 'Program office updated successfully', 'success');
        resetProgramOfficeCache();
        onSave?.();
      } else {
        setLocalError('Error updating program office');
        showToast(true, 'Error updating program office', 'error');
      }
    }
  };

  return (
    <FormControl fullWidth sx={{ backgroundColor: "#F3F4F3", padding: "20px", marginBottom: 0 }}>
      {errorUseAllUsers && <Alert variant="filled" severity="error">{errorUseAllUsers}</Alert>}
      {localError && <Alert variant="filled" severity="error">{localError}</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
              <Button variant="outlined" color="error" onClick={onDelete}>
                Disable Office
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Program Office Name"
            value={editedAccount.title ?? ''}
            disabled={editedAccount.title === undefined}
            onChange={(e) => {
              setEditedAccount({ ...editedAccount, title: e.target.value });
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Acronym"
            value={editedAccount.acronym ?? ''}
            disabled={editedAccount.acronym === undefined}
            onChange={(e) => {
              setEditedAccount({ ...editedAccount, acronym: e.target.value });
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Status"
            select
            value={editedAccount.status ?? 'ACTIVE'}
            disabled={editedAccount.status === undefined}
            onChange={(e) => {
              const newStatus = e.target.value as ProgramOfficeStatus;
              setEditedAccount({ ...editedAccount, status: newStatus });
            }}
            >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <FormLabel>Level One Approver(s)</FormLabel>
              <ApproverCheckboxes
                users={allApproverUsers(editedAccount.id ?? 0, 1)}
                selectedApprovers={editedAccount.levelOneApprover || []}
                onApproversChange={(approvers) => {
                  setEditedAccount({ ...editedAccount, levelOneApprover: approvers });
                }}
                approverLevel={1}
              />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <FormLabel>Level Two Approver(s)</FormLabel>
              <ApproverCheckboxes
                users={allApproverUsers(editedAccount.id ?? 0, 2)}
                selectedApprovers={[]}
                approverLevel={2}
              />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <FormLabel>Level Three Approver(s)</FormLabel>
              <ApproverCheckboxes
                users={allApproverUsers(editedAccount.id ?? 0, 3)}
                selectedApprovers={[]}
                approverLevel={3}
              />
          </FormControl>
        </Grid>
      </Grid>
      {renderToast()}
    </FormControl>
  );
}; 